import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Icon,
  Image
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import heroStudents from '../../assets/images/hero-students.jpg';

const MotionBox = motion(Box);

/**
 * Course Hero Section
 * First impression - title, value prop, CTAs, trust elements
 * Similar layout to beginner-track hero
 */
const CourseHero = ({ data, startDate, onEnrollClick, courseId }) => {
  if (!data) return null;

  const {
    badge,
    title,
    subtitle,
    description,
    ctaButton,
    secondaryButton,
    backgroundImage,
    trustElements = [],
    heroImage,
    theme = {}
  } = data;

  const primaryColor = theme.primaryColor || '#FF6B35';
  const accentColor = theme.accentColor || '#004E89';

  return (
    <Box
      position="relative"
      overflow="hidden"
      bgGradient={backgroundImage ? `linear(to-br, ${primaryColor}, #1a1a2e, ${accentColor})` : undefined}
      bgSize="cover"
      bgPosition="center"
      bgAttachment={backgroundImage ? 'fixed' : undefined}
    >

      {/* Decorative blurred circles */}
      <Box
        position="absolute"
        top="10%"
        right="10%"
        w="300px"
        h="300px"
        bg="accent.gold"
        opacity="0.1"
        borderRadius="full"
        filter="blur(80px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="20%"
        left="5%"
        w="250px"
        h="250px"
        bg="white"
        opacity="0.05"
        borderRadius="full"
        filter="blur(80px)"
        pointerEvents="none"
      />

      <Container maxW="container.xl" pt={{ base: 16, md: 24 }} pb={20} position="relative" zIndex={2}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
          {/* Left Column - Text Content */}
          <MotionBox
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <VStack align="start" spacing={6}>
              {/* Badge */}
              {badge && (
                <Badge
                  mb={2}
                  px={4}
                  py={2}
                  bg="accent.gold"
                  color={primaryColor}
                  fontSize="sm"
                  fontWeight="700"
                  borderRadius="full"
                >
                  {badge}
                </Badge>
              )}

              {/* Title */}
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
                fontWeight="900"
                color="white"
                lineHeight="1.1"
              >
                {title}
              </Heading>

              {/* Subtitle */}
              {subtitle && (
                <Heading
                  as="h2"
                  size="lg"
                  fontWeight="600"
                  color="whiteAlpha.900"
                  lineHeight="1.4"
                >
                  {subtitle}
                </Heading>
              )}

              {/* Description */}
              {description && (
                <Text fontSize="xl" color="whiteAlpha.900" lineHeight="1.8">
                  {description}
                </Text>
              )}

              {/* Trust Elements */}
              {trustElements.length > 0 && (
                <HStack spacing={6} mb={2} flexWrap="wrap">
                  {trustElements.map((elem, idx) => (
                    <HStack key={idx} color="white">
                      <Icon
                        as={elem.icon === 'checkCircle' ? FaCheckCircle : FaCheckCircle}
                        boxSize={5}
                        color="accent.gold"
                      />
                      <Text fontWeight="600">{elem.text}</Text>
                    </HStack>
                  ))}
                </HStack>
              )}

              {/* Start Date */}
              {startDate && (
                <HStack spacing={2}>
                  <Text fontSize="sm" color="whiteAlpha.800" fontWeight="500">
                    📅 Starts:
                  </Text>
                  <Text fontSize="sm" color="white" fontWeight="700">
                    {new Date(startDate).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Text>
                </HStack>
              )}

              {/* CTA Buttons */}
              <HStack spacing={4} flexWrap="wrap">
                {/* Primary Button */}
                {ctaButton && (
                  <Button
                    as={ctaButton.href ? 'a' : 'button'}
                    href={ctaButton.href?.replace('{courseId}', courseId)}
                    onClick={!ctaButton.href ? onEnrollClick : undefined}
                    bg="accent.gold"
                    color={primaryColor}
                    size="lg"
                    h="56px"
                    px={8}
                    fontWeight="700"
                    borderRadius="lg"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                    _active={{ transform: 'translateY(0)' }}
                    transition="all 0.2s"
                  >
                    {ctaButton.text}
                  </Button>
                )}

                {/* Secondary Button */}
                {secondaryButton && (
                  <Button
                    as="a"
                    href={secondaryButton.href?.replace('{courseId}', courseId) || secondaryButton.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline"
                    borderColor="white"
                    color="white"
                    size="lg"
                    h="56px"
                    px={8}
                    fontWeight="700"
                    borderRadius="lg"
                    leftIcon={secondaryButton.icon === 'whatsapp' ? <FaWhatsapp /> : undefined}
                    _hover={{
                      bg: 'whiteAlpha.100',
                      transform: 'translateY(-2px)'
                    }}
                    transition="all 0.2s"
                  >
                    {secondaryButton.text}
                  </Button>
                )}
              </HStack>
            </VStack>
          </MotionBox>

          {/* Right Column - Image Section */}
          <MotionBox
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box
              bg="whiteAlpha.200"
              borderRadius="2xl"
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor="whiteAlpha.300"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              cursor="pointer"
              _hover={{ transform: 'scale(1.05)' }}
              transition="all 0.3s"
              overflow="hidden"
              minH="400px"
            >
              <Image
                src={heroImage || heroStudents}
                alt="Course hero - Students learning"
                w="100%"
                h="100%"
                objectFit="cover"
                borderRadius="lg"
              />
            </Box>
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CourseHero;
